import { StopOutlined } from "@ant-design/icons";
import type { ListColumn } from "../../../../../shared/frontend/list/listColumn";
import { Button, Form, Input, InputNumber, Select, Typography } from "../../../../../shared/frontend/ui";
import React from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../app/i18n/useI18n";
import { formatDateOnly, formatMoney } from "../app/units/format";
import { useDecimalSeparator } from "../app/units/useDecimalSeparator";
import { useEffectiveUnits } from "../app/units/useEffectiveUnits";
import { DrawerSelectionList } from "./DrawerSelectionList";
import { FormDrawer } from "./FormDrawer";
import { useContributionCancellation, type CancellableContribution, type ContributionCancellationActionProps } from "./hooks/useContributionCancellation";

/** Beträge unter einem halben Cent gelten als beglichen. */
const MONEY_EPSILON = 0.005;

export function ContributionCancellationAmount({ amount, cancelledAmount = 0 }: { amount: number; cancelledAmount?: number }) {
  const units = useEffectiveUnits();
  const { text } = useI18n();
  return <>{formatMoney(amount, units)}{cancelledAmount > 0 ? <div><Typography.Text type="secondary">
    {text(cancelledAmount < amount - MONEY_EPSILON ? "openItems.cancel.partial" : "Open item status cancelled")}: {formatMoney(cancelledAmount, units)}
  </Typography.Text></div> : null}</>;
}

/** Same selection/form pattern as PaymentDrawer, with contribution-cancellation fields. */
export function OpenItemCancellationAction(props: ContributionCancellationActionProps) {
  const { text } = useI18n();
  const units = useEffectiveUnits();
  const decimalSeparator = useDecimalSeparator();
  const formId = React.useId();
  const state = useContributionCancellation(props);
  const columns: ListColumn<CancellableContribution>[] = [
    ...(!props.personId ? [{ title: text("Person"), dataIndex: "personName", key: "personName",
      sorter: (a: CancellableContribution, b: CancellableContribution) => a.personName.localeCompare(b.personName) }] : []),
    { title: text("Description"), dataIndex: "description", key: "description",
      sorter: (a, b) => a.description.localeCompare(b.description) },
    { title: text("Due date"), dataIndex: "dueDate", key: "dueDate",
      sorter: (a, b) => a.dueDate.localeCompare(b.dueDate), render: (_, row) => formatDateOnly(row.dueDate, units) },
    { title: text("Cancellable amount"), dataIndex: "remainingAmount", key: "remainingAmount", align: "right",
      sorter: (a, b) => a.remainingAmount - b.remainingAmount, render: (_, row) => formatMoney(row.remainingAmount, units) },
    ...(state.isPartial ? [{ title: text("Cancellation allocation"), key: "allocation", align: "right" as const,
      sorter: (a: CancellableContribution, b: CancellableContribution) => (state.allocations.get(a.id) ?? 0) - (state.allocations.get(b.id) ?? 0),
      render: (_: unknown, row: CancellableContribution) => formatMoney(state.allocations.get(row.id) ?? 0, units) }] : [])
  ];
  if (!state.auth.hasAnyPermission("openItems.write")) return null;
  return <>
    <Button danger icon={<StopOutlined />} type={props.compact ? "link" : "default"} size={props.compact ? "small" : "middle"}
      onClick={() => state.show()}>{text("openItems.cancel.button")}</Button>
    <FormDrawer dirty={state.dirty} title="openItems.cancel.title" open={state.open} onCancel={state.close} formId={formId}
      contentLoading={state.loading} submitting={state.saving} dangerous submitDisabled={!state.canSubmit}
      submitText="openItems.cancel.confirm" submitIcon={<StopOutlined />}>
      <Form id={formId} form={state.form} layout="vertical" requiredMark={false} onFinish={(values) => state.save(values)}>
        <DrawerSelectionList<CancellableContribution> title={text("Select contributions")} dataSource={state.items} columns={columns} rowKey="id"
          emptyText={<Typography.Text type="secondary">{text(state.loadFailed ? "Error loading items" : "openItems.cancel.none")}</Typography.Text>}
          rowSelection={{ selectedRowKeys: state.selectedIds, hideSelectAll: false, selectionByRowClick: true,
            onChange: (keys) => state.select(keys) }} />
        <Form.Item name="amount" label={text("openItems.cancel.amount")} rules={[
          { required: true, message: text("Required") },
          { type: "number", min: 0.01, max: state.maxAmount, message: text("E_76cbcc883743aa02") }
        ]}><InputNumber min={0.01} max={state.maxAmount} precision={2} step={0.01} decimalSeparator={decimalSeparator} style={{ width: "100%" }} /></Form.Item>
        <Form.Item name="reasonId" label={text("Contribution cancellation reason")} rules={[{ required: true, message: text("E_76cbcc883743aa05") }]}>
          <Select showSearch optionFilterProp="label" options={state.reasons.map((reason) => ({ value: reason.id, label: reason.name }))} />
        </Form.Item>
        {!state.loading && !state.loadFailed && state.reasons.length === 0 ? <Typography.Paragraph type="secondary">
          {text("No contribution cancellation reasons available")}
          {state.auth.hasAnyPermission("contributionCancellationReasons.write") ? <> {" "}
            <Link to="/settings/payment-cancellation-reasons?tab=contributions">{text("Manage contribution cancellation reasons")}</Link></> : null}
        </Typography.Paragraph> : null}
        <Form.Item name="reason" label={text("Cancellation explanation")} rules={[{ max: 500, message: text("E_76cbcc883743aa03") }]}>
          <Input.TextArea rows={3} maxLength={500} />
        </Form.Item>
      </Form>
    </FormDrawer>
  </>;
}
